import { ref, get, set, push, update, runTransaction, query, orderByChild, equalTo } from 'firebase/database';
import { db } from '../lib/firebase';
import { FirebaseError } from 'firebase/app';
import { Credit, CreditPurchase, CreditUsage, CreditPackage, FirebaseCreditData } from '../types/credits';
import { stripeService } from './stripeService';

export const getUserCredits = async (userId: string): Promise<number> => {
  const creditsRef = ref(db, `credits/${userId}`);
  const snapshot = await get(creditsRef);
  
  if (!snapshot.exists()) {
    return 0;
  }

  const credits = Object.values(snapshot.val() as Record<string, Credit>);
  return credits.reduce((total, credit) => total + credit.amount, 0);
};

// Cette fonction est maintenant gérée par le webhook Stripe
// L'achat est initié directement via stripeService.createCheckoutSession
// et les crédits sont ajoutés automatiquement par le webhook

export const checkPurchaseStatus = async (sessionId: string): Promise<string> => {
  try {
    // Appeler une fonction Netlify pour vérifier le statut
    // Cette fonction sera créée si nécessaire
    const response = await fetch('/.netlify/functions/check-payment-status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ sessionId })
    });

    if (!response.ok) {
      throw new Error('Erreur lors de la vérification du statut');
    }

    const data = await response.json();
    return data.status;
  } catch (err) {
    console.error('Erreur Stripe:', err);
    throw new Error('Erreur lors de la vérification du paiement');
  }
};

// Cette fonction n'est plus nécessaire car le webhook gère automatiquement
// l'ajout des crédits lors du paiement réussi

export const getCreditHistory = async (userId: string): Promise<CreditPurchase[]> => {
  try {
    const purchasesRef = ref(db, 'credit_purchases');

    // Utiliser une requête indexée pour filtrer côté serveur
    const userPurchasesQuery = query(
      purchasesRef,
      orderByChild('userId'),
      equalTo(userId)
    );

    const snapshot = await get(userPurchasesQuery);

    if (!snapshot.exists()) {
      return [];
    }

    // Mapper les résultats et trier par date décroissante
    return Object.entries(snapshot.val())
      .map(([id, data]) => ({
        id,
        ...(data as Omit<CreditPurchase, 'id'>)
      }))
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  } catch (error) {
    console.error('Erreur lors de la récupération de l\'historique:', error);
    throw new Error('Impossible de récupérer l\'historique des achats');
  }
};

/**
 * Consomme un nombre spécifié de crédits pour un utilisateur
 * @param userId ID de l'utilisateur
 * @param quantity Nombre de crédits à consommer
 * @param metadata Métadonnées optionnelles sur l'utilisation (type d'opération, etc.)
 * @returns Promise<void>
 * @throws Error si le solde est insuffisant ou en cas d'erreur de transaction
 *
 * Consomme un nombre spécifié de crédits pour un utilisateur
 * @param userId ID de l'utilisateur
 * @param quantity Nombre de crédits à consommer
 * @param metadata Métadonnées optionnelles sur l'utilisation
 * @throws {Error} Erreur détaillée selon le type d'erreur rencontré
 */
export const consumeCredits = async (
  userId: string,
  quantity: number,
  metadata?: {
    estimateId?: string;
    operationType?: string;
    address?: string;
  }
): Promise<void> => {
  console.log('[consumeCredits] DÉBUT - userId:', userId, 'quantité:', quantity);

  if (!userId) {
    console.error('[consumeCredits] ID utilisateur manquant');
    throw new Error('ID utilisateur requis');
  }

  if (quantity <= 0) {
    console.error('[consumeCredits] Quantité invalide:', quantity);
    throw new Error('La quantité doit être supérieure à 0');
  }

  const creditsRef = ref(db, `credits/${userId}`);

  try {
    console.log('[consumeCredits] Vérification du solde initial...');

    // Vérifier le solde initial
    const snapshot = await get(creditsRef);
    const currentCredits = snapshot.val();

    console.log('[consumeCredits] État initial des crédits:', {
      exists: snapshot.exists(),
      raw: currentCredits,
      parsed: JSON.stringify(currentCredits, null, 2),
    });

    // Calculer le solde total
    const totalCredits = currentCredits
      ? Object.values(currentCredits).reduce(
          (sum: number, credit: any) => sum + (credit.amount || 0),
          0
        )
      : 0;

    console.log('[consumeCredits] Vérification du solde:', {
      disponible: totalCredits,
      demandé: quantity,
      suffisant: totalCredits >= quantity
    });

    if (totalCredits < quantity) {
      console.error('[consumeCredits] SOLDE INSUFFISANT');
      throw new Error('INSUFFICIENT_CREDITS');
    }

    console.log('[consumeCredits] Validation de la structure des crédits...');

    const invalidCredits: string[] = [];
    if (currentCredits) {
      for (const [id, credit] of Object.entries(currentCredits)) {
        const creditData = credit as any;
        if (!creditData.userId || !creditData.createdAt || !creditData.purchaseId) {
          console.error(`[consumeCredits] Crédit ${id} invalide:`, {
            hasUserId: !!creditData.userId,
            hasCreatedAt: !!creditData.createdAt,
            hasPurchaseId: !!creditData.purchaseId,
            data: creditData
          });
          invalidCredits.push(id);
        }
        if (typeof creditData.amount !== 'number' || creditData.amount < 0) {
          console.error(`[consumeCredits] Crédit ${id} avec amount invalide:`, creditData.amount);
          invalidCredits.push(id);
        }
      }
    }

    if (invalidCredits.length > 0) {
      console.error('[consumeCredits] Crédits invalides détectés:', invalidCredits);
      throw new Error('INVALID_CREDITS_STRUCTURE');
    }

    console.log('[consumeCredits] Validation réussie, démarrage de la transaction Firebase...');

    // Exécuter une transaction pour décrémenter les crédits
    const transactionResult = await runTransaction(creditsRef, (credits) => {
      console.log('[consumeCredits] Transaction - état actuel:', credits);

      if (!credits) {
        console.log('[consumeCredits] Transaction - aucun crédit trouvé, retour {}');
        return {};
      }

      let remainingToDeduct = quantity;
      const updatedCredits = { ...credits };

      console.log('[consumeCredits] Transaction - début de la déduction');

      for (const [id, credit] of Object.entries(updatedCredits)) {
        if (remainingToDeduct <= 0) {
          console.log('[consumeCredits] Transaction - déduction complète, arrêt');
          break;
        }

        const creditData = credit as any;
        const currentAmount = creditData.amount || 0;

        if (typeof currentAmount !== 'number' || currentAmount < 0) {
          console.error(`[consumeCredits] Transaction - Crédit invalide détecté pour ${id}:`, credit);
          continue;
        }

        if (!creditData.userId || !creditData.createdAt || !creditData.purchaseId) {
          console.error(`[consumeCredits] Transaction - Crédit incomplet détecté pour ${id}:`, {
            userId: creditData.userId,
            createdAt: creditData.createdAt,
            purchaseId: creditData.purchaseId
          });
          continue;
        }

        const deduction = Math.min(currentAmount, remainingToDeduct);
        console.log(`[consumeCredits] Transaction - Déduction ${deduction} sur le crédit ${id} (montant: ${currentAmount})`);

        const newCreditData: FirebaseCreditData = {
          userId: creditData.userId,
          amount: currentAmount - deduction,
          createdAt: creditData.createdAt,
          purchaseId: creditData.purchaseId,
          updatedAt: new Date().toISOString(),
        };

        updatedCredits[id] = newCreditData;

        if (newCreditData.amount === 0) {
          console.log(`[consumeCredits] Transaction - Crédit ${id} épuisé (amount: 0)`);
        }

        remainingToDeduct -= deduction;
      }

      console.log('[consumeCredits] Transaction - Mise à jour calculée:', {
        avant: Object.keys(credits).length,
        après: Object.keys(updatedCredits).length,
        restantÀDéduire: remainingToDeduct
      });

      return updatedCredits;
    });

    console.log('[consumeCredits] Transaction terminée avec succès:', {
      committed: transactionResult.committed,
      snapshot: transactionResult.snapshot.exists()
    });

    // Enregistrer l'utilisation du crédit
    if (metadata) {
      console.log('[consumeCredits] Enregistrement de l\'utilisation...');

      const usageRef = push(ref(db, `credit_usage/${userId}`));
      const usage: CreditUsage = {
        id: usageRef.key!,
        userId,
        quantity,
        usedAt: new Date().toISOString(),
        ...metadata,
      };

      await set(usageRef, usage);
      console.log("[consumeCredits] Utilisation enregistrée:", usage);
    }

    // Petit délai pour laisser les listeners Firebase se mettre à jour
    await new Promise(resolve => setTimeout(resolve, 500));

    console.log('[consumeCredits] FIN - Succès');

  } catch (error: any) {
    console.error('[consumeCredits] ERREUR CRITIQUE:', {
      error,
      type: error?.constructor?.name,
      message: error?.message,
      stack: error?.stack,
    });

    // Gestion des erreurs Firebase spécifiques
    if (error instanceof FirebaseError) {
      console.error('[consumeCredits] Erreur Firebase détectée:', error.code);
      switch (error.code) {
        case 'PERMISSION_DENIED':
          throw new Error('Accès refusé aux crédits');
        case 'DISCONNECTED':
          throw new Error('Erreur de connexion');
        default:
          throw new Error(`Erreur Firebase: ${error.message}`);
      }
    }

    // Gestion des erreurs personnalisées
    if (error.message === 'INSUFFICIENT_CREDITS') {
      console.error('[consumeCredits] Solde insuffisant confirmé');
      throw new Error('Solde de crédits insuffisant');
    }

    if (error.message === 'INVALID_CREDITS_STRUCTURE') {
      console.error('[consumeCredits] Structure de crédits invalide détectée');
      throw new Error('Vos crédits ont une structure invalide. Veuillez contacter le support.');
    }

    // Erreur par défaut
    console.error('[consumeCredits] Erreur inconnue, propagation');
    throw new Error('Erreur lors de la mise à jour des crédits');
  }
};